import cx from 'classnames';

export interface SettingsGroupItemProps
  extends React.HTMLAttributes<HTMLDivElement> {
  isSeparated?: boolean;
  disabled?: boolean;
}

/**
 * Enter and Space press a clickable row as they press a button. Only keys aimed
 * at the row itself, so a control inside it keeps its own.
 */
function pressOnEnterOrSpace(
  onKeyDown: React.KeyboardEventHandler<HTMLDivElement> | undefined,
): React.KeyboardEventHandler<HTMLDivElement> {
  return (event) => {
    onKeyDown?.(event);

    if (
      !event.defaultPrevented &&
      !event.repeat &&
      event.target === event.currentTarget &&
      (event.key === 'Enter' || event.key === ' ')
    ) {
      event.preventDefault();
      event.currentTarget.click();
    }
  };
}

export function SettingsGroupItem({
  className,
  isSeparated,
  disabled,
  onKeyDown,
  ...rest
}: SettingsGroupItemProps) {
  // A clickable row is a button: in the tab order, and pressed from the keyboard.
  const buttonProps = rest.onClick
    ? { role: 'button', tabIndex: 0, onKeyDown: pressOnEnterOrSpace(onKeyDown) }
    : { onKeyDown };

  return (
    <div
      {...buttonProps}
      aria-disabled={disabled}
      /*
       * Marks the row as the unit a setting is presented in. Undo/redo flashes
       * the row that changed, which it can only do if it can find one: several
       * controls — the switch above all, whose real input is a transparent
       * overlay — are not themselves visible enough to highlight.
       */
      data-settings-row=""
      {...rest}
      className={cx(
        className,
        // the border is inset from the edge of the element by 16px
        !isSeparated &&
          '[border-image:linear-gradient(90deg,transparent_16px,var(--divider-primary)_16px_calc(100%_-_16px),transparent_calc(100%_-_16px))_1_0]',
        isSeparated && 'tw-rounded-10',
        isSeparated && 'tw-surface-elevated',
        isSeparated && 'tw-shadow-30',
        'tw-px-16',
        'tw-flex',
        'tw-flex-row',
        'tw-items-center',
        'tw-min-h-[44px]',
        'tw-text-ellipsis',
        'tw-icon-28',
        'tw-gap-8',
        'aria-disabled:tw-text-label-quaternary',
        'aria-disabled:tw-cursor-not-allowed',
      )}
    />
  );
}
